import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format, isWeekend, parseISO, isValid } from "date-fns";

/**
 * Working-day date picker — disables weekends and any date before `minDate`.
 * Returns ISO date string (YYYY-MM-DD) via onChange.
 */
export default function WorkingDayPicker({ value, onChange, placeholder = "Pick a date", minDate, testId, disabled, allowWeekends = false }) {
  const [open, setOpen] = useState(false);
  const selected = value && isValid(parseISO(value)) ? parseISO(value) : undefined;

  const disabledMatcher = (d) => {
    if (!allowWeekends && isWeekend(d)) return true;
    if (minDate && d < minDate) return true;
    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`rounded-sm w-full justify-start font-normal text-left ${!selected ? "text-slate-400" : ""}`}
          data-testid={testId}
        >
          <CalendarIcon size={14} className="mr-2 shrink-0" />
          {selected ? format(selected, "EEE, dd MMM yyyy") : placeholder}
          {!allowWeekends && <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">working days only</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }}
          disabled={disabledMatcher}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/** Count working days (Mon–Fri) between two ISO date strings (inclusive). */
export function workingDaysBetween(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const s = parseISO(startIso);
  const e = parseISO(endIso);
  if (!isValid(s) || !isValid(e) || e < s) return 0;
  let count = 0;
  const cursor = new Date(s);
  while (cursor <= e) {
    if (!isWeekend(cursor)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}
